import { Icon, type IconName } from "@elaniintc/goes-design-system";
import ValueDisplay from "./value-display";
import type { RenderProps } from "./types";
import { getColumnTypeIcon } from "../form-correction-table/utils";

type TableColumn = {
  name?: string;
  key: string;
  type?: string;
};

type TableRow = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Celda de una respuesta de tipo tabla.
 *
 * Casi todas las celdas llegan como escalar, pero un telefono con selector de
 * pais y una identificacion con selector de tipo viajan como objeto para no
 * perder esa eleccion. Sin tratarlos aparte, `String()` los pintaria como
 * `[object Object]`.
 *
 * Las filas guardadas antes de que backend aceptara objetos siguen llegando
 * como escalar, asi que ambas formas tienen que convivir.
 *
 * El formato es el mismo que usa el engine al pintar la fila en el formulario:
 * quien revisa ve lo mismo que escribio el ciudadano.
 */
/**
 * Texto de un valor escalar.
 *
 * Un objeto no tiene representacion propia: dejarlo caer en `String()` lo
 * convertiria en "[object Object]", que es justo la regresion que este modulo
 * existe para evitar. Por eso el unico camino a texto pasa por aqui, que
 * reconoce lo escalar y descarta el resto.
 */
const toText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "bigint" ||
    typeof value === "boolean"
  ) {
    return value.toString();
  }
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(", ");
  return "";
};

const formatCell = (column: TableColumn, value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (column.type === "switch" || typeof value === "boolean") {
    return value === true || value === "true" ? "Sí" : "No";
  }
  if (isRecord(value)) {
    if ("number" in value) {
      const code = value.calling_code
        ? `(+${toText(value.calling_code)}) `
        : "";
      return `${code}${toText(value.number)}`;
    }
    if ("value" in value) return toText(value.value ?? "—");
  }
  return toText(value);
};

/** Columnas que el diseno deja visibles antes de recurrir al scroll. */
const VISIBLE_COLUMNS = 4;

/**
 * Ancho de la tabla para que entren exactamente `VISIBLE_COLUMNS` en el espacio
 * disponible y el resto quede en scroll horizontal.
 *
 * Se calcula en porcentaje y no en pixeles para que cada columna ocupe siempre
 * la misma fraccion del contenedor, que es estrecho en el detalle y ancho en el
 * resumen. Con 4 columnas o menos la tabla no desborda y ocupa el ancho completo.
 */
const tableWidth = (columnCount: number): string =>
  `${Math.max(100, (columnCount / VISIBLE_COLUMNS) * 100)}%`;

/** Respuesta de un campo de tipo tabla en las vistas de solo lectura. */
export default function tableRenderer({ obj, value, isOldValue }: RenderProps) {
  const columns = (obj.config?.columns ?? []) as TableColumn[];
  const rows = Array.isArray(value) ? (value as TableRow[]) : [];

  if (columns.length === 0 || rows.length === 0) {
    return <ValueDisplay isOldValue={isOldValue}>Sin registros</ValueDisplay>;
  }

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
      <table
        className="table-fixed border-collapse text-size-body"
        style={{ width: tableWidth(columns.length) }}
      >
        <thead>
          {/* El borde va en las celdas y no en la fila: con `border-collapse`
              los bordes del `tr` compiten con los de las celdas y pueden
              perder, y el encabezado quedaba sin su linea inferior. */}
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="border-b border-neutral-silent px-1.5 py-0.5 text-left align-middle first:pl-3 last:pr-3"
              >
                {/* `flex-wrap`: el icono queda junto al nombre cuando hay
                    ancho, y encima cuando la columna es angosta. */}
                <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                  <Icon
                    type="far"
                    name={getColumnTypeIcon(column.type) as IconName}
                    size="sm"
                    className="text-body shrink-0"
                  />
                  <span className="min-w-0 flex-1 font-semibold text-body wrap-anywhere">
                    {column.name || column.key}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${obj.id}-row-${index + 1}`}
              className="border-b border-neutral-silent last:border-b-0"
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className="px-1.5 py-0.5 align-top wrap-anywhere first:pl-3 last:pr-3"
                >
                  <ValueDisplay isOldValue={isOldValue}>
                    {formatCell(column, row?.[column.key])}
                  </ValueDisplay>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
